<template>
    <div class="billing-entity-modal">
        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.name') }}
                    {{ isFieldRequired('name') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    ref="nameInput"
                    name="name"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.name"
                    v-model="billingEntityStore.billingEntityData.name"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                    :condensed="true"
                >
                    {{ $t('§billing_entity.logo') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <div class="flex-grow-1 flex-shrink-1 flex-basis-60">
                    <CoreFileUpload
                        ref="logoUpload"
                        name="logo"
                        :accept="'image/png,image/jpeg,image/jpg,image/svg+xml'"
                        :uploadUrl="uploadUrl"
                        :filePath="billingEntityStore.billingEntityData.logo_url"
                        :disabled="billingEntityStore.saving"
                        :metadata="getUploadMetadata()"
                        @file-added="handleLogoAdded"
                        @uploaded="handleLogoUploaded"
                        @download="handleLogoDownload"
                        @delete="handleLogoDelete"
                    />
                </div>
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.vat_number') }}
                    {{ isFieldRequired('vat_number') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="vat_number"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.vat_number"
                    v-model="billingEntityStore.billingEntityData.vat_number"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.company_registration_number') }}
                    {{ isFieldRequired('company_registration_number') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="company_registration_number"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.company_registration_number"
                    v-model="billingEntityStore.billingEntityData.company_registration_number"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.address_line1') }}
                    {{ isFieldRequired('address_line1') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="address_line1"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.address_line1"
                    v-model="billingEntityStore.billingEntityData.address_line1"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.address_line2') }}
                    {{ isFieldRequired('address_line2') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="address_line2"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    v-model="billingEntityStore.billingEntityData.address_line2"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.postal_code') }}
                    {{ isFieldRequired('postal_code') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="postal_code"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.postal_code"
                    v-model="billingEntityStore.billingEntityData.postal_code"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.city') }}
                    {{ isFieldRequired('city') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="city"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.city"
                    v-model="billingEntityStore.billingEntityData.city"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.country') }}
                    {{ isFieldRequired('country') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="country"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.country"
                    v-model="billingEntityStore.billingEntityData.country"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_name') }}
                    {{ isFieldRequired('bank_name') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_name"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.bank_name"
                    v-model="billingEntityStore.billingEntityData.bank_name"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_iban') }}
                    {{ isFieldRequired('bank_iban') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_iban"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.bank_iban"
                    v-model="billingEntityStore.billingEntityData.bank_iban"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_bic') }}
                    {{ isFieldRequired('bank_bic') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_bic"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    :validator="v$.bank_bic"
                    v-model="billingEntityStore.billingEntityData.bank_bic"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_address_line1') }}
                    {{ isFieldRequired('bank_address_line1') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_address_line1"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    v-model="billingEntityStore.billingEntityData.bank_address_line1"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_address_line2') }}
                    {{ isFieldRequired('bank_address_line2') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_address_line2"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    v-model="billingEntityStore.billingEntityData.bank_address_line2"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_postal_code') }}
                    {{ isFieldRequired('bank_postal_code') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_postal_code"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    v-model="billingEntityStore.billingEntityData.bank_postal_code"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.bank_city') }}
                    {{ isFieldRequired('bank_city') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreStringInput
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="bank_city"
                    :condensed="true"
                    :disabled="billingEntityStore.saving"
                    v-model="billingEntityStore.billingEntityData.bank_city"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.show_name_on_top') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreCheckbox
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="show_name_on_top"
                    v-model="billingEntityStore.billingEntityData.show_name_on_top"
                    :disabled="billingEntityStore.saving"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreTwoColumnsRow class="mb-3">
            <template #left>
                <CoreBorderWrapper
                    :condensed="true"
                    class="flex-grow-1 flex-shrink-1 flex-basis-40"
                >
                    {{ $t('§billing_entity.show_serviced_by') }}
                </CoreBorderWrapper>
            </template>
            <template #right>
                <CoreCheckbox
                    class="flex-grow-1 flex-shrink-1 flex-basis-60"
                    name="show_serviced_by"
                    v-model="billingEntityStore.billingEntityData.show_serviced_by"
                    :disabled="billingEntityStore.saving"
                />
            </template>
        </CoreTwoColumnsRow>

        <div class="flex justify-start gap-2">
            <CoreButton
                label="§ui_button.save"
                icon="pi-save"
                @click="handleSave"
                :loading="billingEntityStore.saving"
                :disabled="!isValid"
            />

            <CoreButton
                variant="tertiary"
                label="§ui_button.cancel"
                @click="modalStore.setClose"
                icon="pi-times"
            />
        </div>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import { useBillingEntityStore } from '@/stores/billingEntity'
import { useModalStore } from '@/stores/modal'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreFileUpload from '@/components/core/CoreFileUpload.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'

const rules = {
    name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(100),
    },
    vat_number: {
        required,
        maxLength: maxLength(25),
    },
    company_registration_number: {
        required,
        maxLength: maxLength(25),
    },
    address_line1: {
        required,
        maxLength: maxLength(200),
    },
    postal_code: {
        required,
        maxLength: maxLength(32),
    },
    city: {
        required,
        maxLength: maxLength(100),
    },
    country: {
        required,
        maxLength: maxLength(100),
    },
    bank_name: {
        required,
        maxLength: maxLength(200),
    },
    bank_iban: {
        required,
        maxLength: maxLength(50),
    },
    bank_bic: {
        required,
        maxLength: maxLength(20),
    },
}

export default {
    name: 'BillingEntityModal',

    components: {
        CoreBorderWrapper,
        CoreTwoColumnsRow,
        CoreStringInput,
        CoreButton,
        CoreFileUpload,
        CoreCheckbox,
    },

    data() {
        return {
            v$: {},
            fileSelected: false,
            logoDeleted: false,
        }
    },

    setup() {
        const modalStore = useModalStore()
        const billingEntityStore = useBillingEntityStore()

        return {
            modalStore,
            billingEntityStore,
        }
    },

    computed: {
        isValid() {
            return !this.v$.$invalid
        },

        isEditMode() {
            return !!this.billingEntityStore.billingEntityData.id
        },

        uploadUrl() {
            if (this.isEditMode) {
                return this.route('billing_entity.update', [this.billingEntityStore.billingEntityData.id])
            }
            return this.route('billing_entity.store')
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.v$ = useVuelidate(rules, this.billingEntityStore.billingEntityData)

            if (this.$refs.nameInput && typeof this.$refs.nameInput.focus === 'function') {
                this.$refs.nameInput.focus()
            }
        })
    },

    beforeUnmount() {
        this.billingEntityStore.resetState()
        this.billingEntityStore.saving = false
    },

    methods: {
        getUploadMetadata() {
            const metadata = {
                name: this.billingEntityStore.billingEntityData.name,
                vat_number: this.billingEntityStore.billingEntityData.vat_number,
                company_registration_number: this.billingEntityStore.billingEntityData.company_registration_number,
                address_line1: this.billingEntityStore.billingEntityData.address_line1,
                address_line2: this.billingEntityStore.billingEntityData.address_line2,
                postal_code: this.billingEntityStore.billingEntityData.postal_code,
                city: this.billingEntityStore.billingEntityData.city,
                country: this.billingEntityStore.billingEntityData.country,
                bank_name: this.billingEntityStore.billingEntityData.bank_name,
                bank_iban: this.billingEntityStore.billingEntityData.bank_iban,
                bank_bic: this.billingEntityStore.billingEntityData.bank_bic,
                bank_address_line1: this.billingEntityStore.billingEntityData.bank_address_line1,
                bank_address_line2: this.billingEntityStore.billingEntityData.bank_address_line2,
                bank_postal_code: this.billingEntityStore.billingEntityData.bank_postal_code,
                bank_city: this.billingEntityStore.billingEntityData.bank_city,
                show_name_on_top: this.billingEntityStore.billingEntityData.show_name_on_top ? 1 : 0,
                show_serviced_by: this.billingEntityStore.billingEntityData.show_serviced_by ? 1 : 0,
            }

            if (this.logoDeleted) {
                metadata.delete_logo = '1'
            }

            return metadata
        },

        handleLogoAdded() {
            this.fileSelected = true
            this.logoDeleted = false
        },

        handleLogoUploaded(xhr) {
            this.billingEntityStore.saving = false
            this.billingEntityStore.billingEntityTableRefreshKey += 1
            this.modalStore.setClose()
        },

        handleLogoDownload() {
            this.billingEntityStore.downloadLogo()
        },

        async handleLogoDelete() {
            this.billingEntityStore.billingEntityData.logo = null
            this.billingEntityStore.billingEntityData.logo_url = null
            this.fileSelected = false
            this.logoDeleted = true
        },

        async handleSave() {
            if (this.billingEntityStore.saving) {
                return
            }

            if (this.$refs.logoUpload) {
                this.billingEntityStore.saving = true

                if (this.fileSelected) {
                    this.$refs.logoUpload.upload()
                } else {
                    try {
                        await this.$refs.logoUpload.uploadMetadataOnly()
                    } catch (error) {
                        this.billingEntityStore.saving = false
                    }
                }
            }
        },
    },
}
</script>
